import React, { useContext } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { TimezoneAbbreviationEnum } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { PagedList } from '_lib/featureComponents/PagedList/PagedList';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

export type AppointmentListProps = {
  companyId: string;
  customerId: string;
  companyTimezone: TimezoneAbbreviationEnum;
  providerJobId?: string;
};

const formatStartEndDateRange = (
  startIsoString: string,
  endIsoString: string | null,
  timezone: TimezoneAbbreviationEnum
) => {
  const startDisplay = DateUtils.format(startIsoString, DateFormatEnum.DateAtTime, { timezone });
  const endDisplay = endIsoString
    ? DateUtils.format(endIsoString, DateFormatEnum.DateAtTime, { timezone })
    : 'Unknown End';

  return `${startDisplay} - ${endDisplay}`;
};

export const AppointmentList = (props: AppointmentListProps) => {
  const { companyId, customerId, companyTimezone, providerJobId } = props;

  const serviceTitanApi = useContext(ApiContext).serviceTitan(companyId);

  const customerAppointmentsQuery = serviceTitanApi.customers
    .appointments(customerId)
    .useListCached({ params: { providerJobId, pageSize: 25, sort: '-modifiedAt' } });

  if (customerAppointmentsQuery.error) {
    return (
      <ManualErrorBoundary
        errorState={{ message: 'Failed to load Appointments' }}
        onRefresh={async () => {
          await customerAppointmentsQuery.refetch();
        }}
        sx={{ padding: 1 }}
      />
    );
  }

  return (
    <Stack
      useFlexGap
      spacing={0}
      sx={{ position: 'relative', minHeight: customerAppointmentsQuery.isFetching ? '180px' : undefined }}>
      <Typography variant='body1Bold' text='Appointments:' sx={{ marginTop: 1 }} />
      {customerAppointmentsQuery.isFetching && <BackdropSpinner open={true} />}
      <PagedList
        queryResult={customerAppointmentsQuery}
        emptyListMessage='Appointments not found'
        disableShowMore
        renderFunction={(appointment) => (
          <ListItem key={appointment.providerId}>
            <Stack useFlexGap spacing={0}>
              <Typography
                variant='body1Bold'
                text={`Start/End: ${formatStartEndDateRange(appointment.start, appointment.end, companyTimezone)}`}
              />
              {appointment.arrivalWindowStart && (
                <Typography
                  text={`Arrival Window: ${formatStartEndDateRange(
                    appointment.arrivalWindowStart,
                    appointment.arrivalWindowEnd,
                    companyTimezone
                  )}`}
                />
              )}
              <Typography text={`Status: ${appointment.status}`} />
              <Typography text={`Instructions: ${appointment.specialInstructions || 'No instructions'}`} />
            </Stack>
          </ListItem>
        )}
      />
    </Stack>
  );
};
